// CRM-LEADS-2026-10-16 §6: the Leads list's lead-type filters. Matt: "if I click PT the facility needs show up too; I want
// all just PTs, no facilities."
//   * Candidates / Facilities first (a facility = an employer or client inquiry); discipline applies to candidates only.
//   * Source: site, Meta, job alerts, job apply, voicemail, chat, manual (job alerts + form_type chat: NIGHT-DIRECTSTAFF-2026-10-17 §2).  * Date range on when the lead came in.
//   * Kept per device (localStorage), so the list opens the way it was left.
export const ANY = 'Any';
export const DISCIPLINES = ['PT', 'PTA', 'OT', 'COTA', 'SLP', 'SLPA'];
export const KINDS = { candidates: 'Candidates', facilities: 'Facilities' };
export const SOURCES = { site: 'Site form', meta: 'Meta ad', alerts: 'Job alerts', job: 'Job apply', voicemail: 'Voicemail', chat: 'Chat', manual: 'Added in the CRM' };
export const FILTER_DEFAULTS = { kind: 'candidates', discipline: ANY, source: ANY, from: '', to: '' };
export const FILTERS_KEY = 'crm.leadFilters.v1';

const FACILITY_FORM_TYPES = new Set(['facility_staffing_request', 'facility_request', 'facility_inquiry', 'facility_lead', 'employer_inquiry']);
const raw = (l) => (l && l.raw_payload && typeof l.raw_payload === 'object' ? l.raw_payload : {});
const s = (v) => (v === null || v === undefined ? '' : String(v).trim());

/** 'facility' for an employer / client inquiry, else 'candidate'. */
export function leadKind(l) {
  if (s(l && l.lead_type).toLowerCase() === 'facility') return 'facility';   // NIGHT-AGENTS-2026-10-21: the leads worker's own marker
  const ft = s(l && l.form_type).toLowerCase();
  if (FACILITY_FORM_TYPES.has(ft) || ft.startsWith('facility')) return 'facility';
  const r = raw(l);
  return s(r.facility_name) || s(r.facility) || s(r.company_name) ? 'facility' : 'candidate';
}

const ALIASES = {
  'PHYSICAL THERAPIST': 'PT', 'PHYSICAL THERAPY': 'PT', 'PHYSICAL THERAPIST ASSISTANT': 'PTA', 'PHYSICAL THERAPY ASSISTANT': 'PTA',
  'OCCUPATIONAL THERAPIST': 'OT', 'OCCUPATIONAL THERAPY': 'OT', 'CERTIFIED OCCUPATIONAL THERAPY ASSISTANT': 'COTA', 'OTA': 'COTA',
  'OCCUPATIONAL THERAPY ASSISTANT': 'COTA', 'SPEECH-LANGUAGE PATHOLOGIST': 'SLP', 'SPEECH LANGUAGE PATHOLOGIST': 'SLP',
  'SPEECH THERAPIST': 'SLP', 'SPEECH-LANGUAGE PATHOLOGY ASSISTANT': 'SLPA', 'SLP-A': 'SLPA', 'SLP ASSISTANT': 'SLPA',
};
/** The lead's discipline as one of the six codes, '' when none or unknown. */
export function disciplineCode(l) {
  const d = s(l && l.discipline).toUpperCase().replace(/\s+/g, ' ');
  if (DISCIPLINES.includes(d)) return d;
  return ALIASES[d] || '';
}

/** How the lead came in: 'site' | 'meta' | 'alerts' | 'job' | 'voicemail' | 'chat' | 'manual'. */
export function sourceKind(l) {
  const r = raw(l);
  const ft = s(l && l.form_type).toLowerCase();
  const site = s(l && l.source_site).toLowerCase();
  if (ft === 'manual' || s(r.entered_via) === 'crm') return 'manual';
  if (ft === 'chat' || s(r.chat_session_id)) return 'chat';
  if (site === 'meta' || ft.startsWith('meta') || s(r.lead_source) === 'meta') return 'meta';
  if (ft === 'job_alert') return 'alerts';
  if (ft === 'job_apply') return 'job';
  if (ft.includes('voicemail') || site === 'voicemail' || site === 'voice') return 'voicemail';
  return 'site';
}

/** The lead's local calendar date (YYYY-MM-DD), for the date range. */
export function localDate(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** True when the lead passes the type, discipline (candidates only), source and date filters. */
export function passesLeadFilters(l, f) {
  const q = { ...FILTER_DEFAULTS, ...(f || {}) };
  const kind = leadKind(l);
  if (q.kind === 'candidates' && kind !== 'candidate') return false;
  if (q.kind === 'facilities' && kind !== 'facility') return false;
  if (q.kind === 'candidates' && q.discipline !== ANY && disciplineCode(l) !== q.discipline) return false;
  if (q.source !== ANY && sourceKind(l) !== q.source) return false;
  const day = localDate(l && l.created_at);
  if (q.from && (!day || day < q.from)) return false;
  if (q.to && (!day || day > q.to)) return false;
  return true;
}

/** { candidates, facilities } -- the toggle's counts. */
export function kindCounts(leads) {
  const c = { candidates: 0, facilities: 0 };
  for (const l of leads || []) c[leadKind(l) === 'facility' ? 'facilities' : 'candidates'] += 1;
  return c;
}

/** Saved per device; anything unrecognised falls back to the default. */
export function readLeadFilters(storage) {
  let saved = {};
  try { saved = JSON.parse((storage && storage.getItem(FILTERS_KEY)) || '{}') || {}; } catch { saved = {}; }
  const f = { ...FILTER_DEFAULTS };
  if (KINDS[saved.kind]) f.kind = saved.kind;
  if (DISCIPLINES.includes(saved.discipline)) f.discipline = saved.discipline;
  if (SOURCES[saved.source]) f.source = saved.source;
  if (/^\d{4}-\d{2}-\d{2}$/.test(saved.from || '')) f.from = saved.from;
  if (/^\d{4}-\d{2}-\d{2}$/.test(saved.to || '')) f.to = saved.to;
  return f;
}
export function writeLeadFilters(storage, f) {
  try { if (storage) storage.setItem(FILTERS_KEY, JSON.stringify(f)); } catch { /* private window */ }
}
export const filtersActive = (f) => Object.keys(FILTER_DEFAULTS).some((k) => k !== 'kind' && f && f[k] !== FILTER_DEFAULTS[k]);
